/**
 * Commercial Spine — Quotation → Sales Order → Delivery Schedule.
 * Backend: routers/commercial.py.
 *
 * The RMC pre-order → order → daily-plan flow, kept deliberately lean for a
 * small plant team: NO approval tiers or pending states anywhere — commit
 * actions (generate order, cancel, delete) use a plain "Are you sure?"
 * confirm to catch accidental clicks, nothing to wait on.
 *
 * Every Challan draws down a Sales Order (order no. is mandatory), so an
 * order's pending quantity is always live — see the drawdown ledger.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, Trash2, FileText, ClipboardList, CalendarDays,
  ArrowRight, ChevronRight,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../components/ConfirmDialog';
import GstinField from '../components/GstinField';
import { apiGet, apiPost, apiPatch, apiDel } from '../lib/api';
import { inrCompact, formatISTDateTime } from '../lib/format';

const TABS = ['Quotations', 'Sales Orders', 'Delivery Schedule'];
const GRADES = ['M10', 'M15', 'M20', 'M25', 'M30', 'M35', 'M40', 'M45', 'M50'];
const PUMPING = ['Without Pump', 'With Pump'];

const inr = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n, d = 2) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
const today = () => new Date().toISOString().slice(0, 10);
const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-700', sent: 'bg-blue-100 text-blue-700',
  won: 'bg-emerald-100 text-emerald-700', lost: 'bg-red-100 text-red-700', expired: 'bg-amber-100 text-amber-700',
  open: 'bg-blue-100 text-blue-700', completed: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-slate-100 text-slate-500',
  planned: 'bg-slate-100 text-slate-700', dispatched: 'bg-amber-100 text-amber-700',
};

function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>;
}

function ModalShell({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-sm w-full ${wide ? 'max-w-3xl' : 'max-w-md'} max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="font-display font-bold text-sm">{title}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function Commercial() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Quotations');
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.includes(t) && t !== tab) setTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setTab = (t) => { setTabState(t); setSearchParams(t === 'Quotations' ? {} : { tab: t }); };

  return (
    <div data-testid="commercial-page">
      <PageHeader overline="Business Development" title="Sales & Orders"
        subtitle="Quotation → Order → Delivery Schedule. Every challan draws down its order." />
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={'relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ' + (tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]')}
              data-testid={`comm-tab-${t.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#1F7A6E]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Quotations' && <QuotationsTab />}
          {tab === 'Sales Orders' && <OrdersTab />}
          {tab === 'Delivery Schedule' && <ScheduleTab />}
        </div>
      </div>
    </div>
  );
}

// ── Quotations ───────────────────────────────────────────────────────────────
function QuotationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // quotation object or 'new'
  const [confirm, ConfirmHost] = useConfirm();

  const load = () => {
    setLoading(true);
    apiGet('/commercial/quotations').then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load quotations')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const genOrder = async (q) => {
    if (!(await confirm({ title: 'Generate Sales Order?', body: `Convert ${q.quotation_no} into a Sales Order? You'll set the contracted quantity on the order.`, confirmLabel: 'Generate Order' }))) return;
    try {
      const r = await apiPost(`/commercial/quotations/${q.id}/generate-order`);
      toast.success(`${r.order.order_no} created — now set quantities on the order`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to generate order'); }
  };
  const remove = async (q) => {
    if (!(await confirm({ title: 'Delete quotation?', body: `Delete ${q.quotation_no}? This cannot be undone.`, confirmLabel: 'Delete', danger: true }))) return;
    try { await apiDel(`/commercial/quotations/${q.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div>
      {ConfirmHost}
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Quotations · {items.length}</div>
        <button onClick={() => setEditing('new')}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="quote-new-btn"><Plus size={13} /> New Quotation</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Quotation No</th><th>Date</th><th>Party</th><th>Site</th>
            <th className="text-right">Grades</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-400">No quotations yet — raise your first one above.</td></tr>}
            {items.map((q) => (
              <tr key={q.id} className="hover:bg-slate-50" data-testid={`quote-row-${q.id}`}>
                <td className="font-mono text-xs text-[#1F7A6E] font-semibold cursor-pointer" onClick={() => setEditing(q)}>{q.quotation_no}</td>
                <td className="text-xs">{formatISTDateTime(q.date)}</td>
                <td className="cursor-pointer" onClick={() => setEditing(q)}>{q.party_name}</td>
                <td className="text-xs text-slate-500">{q.project_site || '—'}</td>
                <td className="text-right tabular-nums">{(q.items || []).length}</td>
                <td><span className={`tag ${STATUS_COLOR[q.status] || ''}`}>{q.status}</span></td>
                <td className="text-right whitespace-nowrap">
                  {!q.converted_order_id && q.status !== 'lost' && (
                    <button onClick={() => genOrder(q)} className="text-xs text-[#1F7A6E] hover:underline mr-3 inline-flex items-center gap-1" data-testid={`quote-genorder-${q.id}`}>
                      <ArrowRight size={11} /> Gen Order
                    </button>
                  )}
                  {q.converted_order_id && <span className="text-[10px] text-emerald-600 mr-3">→ ordered</span>}
                  {!q.converted_order_id && <button onClick={() => remove(q)} className="text-slate-300 hover:text-red-600" title="Delete"><Trash2 size={13} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <QuotationModal quote={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function QuotationModal({ quote, onClose, onSaved }) {
  const [terms, setTerms] = useState([]);
  const [form, setForm] = useState(quote || {
    date: today(), quotation_type: 'Local Quotation', party_name: '', party_address: '', party_gstin: '',
    contact_person: '', mobile: '', email: '', project_site: '', site_address: '',
    valid_date: '', cement_basic_rate: '', source_note: '', remarks: '',
    items: [{ grade: 'M25', pumping: 'Without Pump', item_name: '', slump: '', max_aggregate: '20', uom: 'CUM', rate: '' }],
    terms: [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setItem = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const addItem = () => setForm({ ...form, items: [...form.items, { grade: 'M25', pumping: 'Without Pump', item_name: '', slump: '', max_aggregate: '20', uom: 'CUM', rate: '' }] });
  const delItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const setTerm = (i, k, v) => { const t = [...(form.terms || [])]; t[i] = { ...t[i], [k]: v }; setForm({ ...form, terms: t }); };
  const addTerm = (bt = '', data = '') => setForm({ ...form, terms: [...(form.terms || []), { business_term: bt, data }] });
  const delTerm = (i) => setForm({ ...form, terms: (form.terms || []).filter((_, idx) => idx !== i) });

  useEffect(() => { apiGet('/commercial/quotation-terms').then((r) => setTerms(r.items || [])).catch(() => {}); }, []);

  const save = async () => {
    if (!form.party_name.trim()) return toast.error('Party name is required');
    if (!form.items.length || form.items.some((i) => !i.grade)) return toast.error('Each line needs a grade');
    setSaving(true);
    try {
      const body = {
        ...form,
        cement_basic_rate: Number(form.cement_basic_rate || 0),
        items: form.items.map((i) => ({ ...i, rate: Number(i.rate || 0) })),
        terms: (form.terms || []).filter((t) => t.business_term && t.data),
      };
      if (quote) await apiPatch(`/commercial/quotations/${quote.id}`, body);
      else await apiPost('/commercial/quotations', body);
      toast.success(quote ? 'Quotation updated' : 'Quotation created');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={quote ? `Edit ${quote.quotation_no}` : 'New Quotation'} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Party / Customer *"><input value={form.party_name} onChange={set('party_name')} className="input" autoFocus data-testid="quote-party" /></Field>
          <Field label="Project Site"><input value={form.project_site} onChange={set('project_site')} className="input" /></Field>
          <Field label="Valid Until"><input type="date" value={(form.valid_date || '').slice(0, 10)} onChange={set('valid_date')} className="input" /></Field>
          <Field label="Contact Person"><input value={form.contact_person} onChange={set('contact_person')} className="input" /></Field>
          <Field label="Mobile"><input value={form.mobile} onChange={set('mobile')} className="input" /></Field>
          <Field label="Cement Basic Rate (₹)"><input type="number" value={form.cement_basic_rate} onChange={set('cement_basic_rate')} className="input" /></Field>
        </div>
        <Field label="Party Address"><input value={form.party_address} onChange={set('party_address')} className="input" /></Field>
        <Field label="Source / Enquiry Note (optional)"><input value={form.source_note} onChange={set('source_note')} className="input" placeholder="e.g. phone enquiry from site engineer" /></Field>

        {/* Grade lines */}
        <div className="border border-slate-200 rounded-sm">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="kpi-overline">Grades Quoted</span>
            <button onClick={addItem} className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1"><Plus size={11} /> Add grade</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead><tr><th>Grade</th><th>Pump</th><th>Slump</th><th>Max Aggr</th><th>Description</th><th className="text-right">Rate ₹/m³</th><th></th></tr></thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={i}>
                    <td><select value={it.grade} onChange={(e) => setItem(i, 'grade', e.target.value)} className="input !py-1">{GRADES.map((g) => <option key={g}>{g}</option>)}</select></td>
                    <td><select value={it.pumping} onChange={(e) => setItem(i, 'pumping', e.target.value)} className="input !py-1">{PUMPING.map((p) => <option key={p}>{p}</option>)}</select></td>
                    <td><input value={it.slump || ''} onChange={(e) => setItem(i, 'slump', e.target.value)} className="input !py-1 w-16" placeholder="mm" /></td>
                    <td><input value={it.max_aggregate || ''} onChange={(e) => setItem(i, 'max_aggregate', e.target.value)} className="input !py-1 w-14" placeholder="20" /></td>
                    <td><input value={it.item_name || ''} onChange={(e) => setItem(i, 'item_name', e.target.value)} className="input !py-1" placeholder="RMC M-25 OPC" /></td>
                    <td><input type="number" value={it.rate} onChange={(e) => setItem(i, 'rate', e.target.value)} className="input !py-1 w-24 text-right" /></td>
                    <td>{form.items.length > 1 && <button onClick={() => delItem(i)} className="text-slate-300 hover:text-red-600"><Trash2 size={12} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Terms */}
        <div className="border border-slate-200 rounded-sm">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="kpi-overline">Business Terms</span>
            <div className="flex items-center gap-2">
              <select onChange={(e) => { const t = terms.find((x) => `${x.business_term}||${x.data}` === e.target.value); if (t) addTerm(t.business_term, t.data); e.target.value=''; }} className="input !py-1 text-xs" defaultValue="">
                <option value="" disabled>+ from library</option>
                {terms.map((t) => <option key={t.id} value={`${t.business_term}||${t.data}`}>{t.business_term}: {t.data.slice(0, 30)}</option>)}
              </select>
              <button onClick={() => addTerm()} className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1"><Plus size={11} /> Custom</button>
            </div>
          </div>
          {(form.terms || []).length === 0 ? <div className="px-3 py-3 text-xs text-slate-400">No terms added — pick from the library or add custom.</div> : (
            <table className="w-full dense-table">
              <tbody>
                {(form.terms || []).map((t, i) => (
                  <tr key={i}>
                    <td className="w-40"><input value={t.business_term} onChange={(e) => setTerm(i, 'business_term', e.target.value)} className="input !py-1" placeholder="Delivery" /></td>
                    <td><input value={t.data} onChange={(e) => setTerm(i, 'data', e.target.value)} className="input !py-1" placeholder="@ your job-site" /></td>
                    <td className="w-8"><button onClick={() => delTerm(i)} className="text-slate-300 hover:text-red-600"><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-1" data-testid="quote-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : (quote ? 'Save Changes' : 'Create Quotation')}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Sales Orders ─────────────────────────────────────────────────────────────
function OrdersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet('/commercial/orders').then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load orders')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Sales Orders · {items.length}</div>
        <button onClick={() => setCreating(true)}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="order-new-btn"><Plus size={13} /> New Order</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Order No</th><th>Date</th><th>Party</th><th>Site</th>
            <th className="text-right">Contracted</th><th className="text-right">Delivered</th><th className="text-right">Pending</th>
            <th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-slate-400">No orders yet. Create one, or convert a quotation.</td></tr>}
            {items.map((o) => (
              <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(o.id)} data-testid={`order-row-${o.id}`}>
                <td className="font-mono text-xs text-[#1F7A6E] font-semibold">{o.order_no}</td>
                <td className="text-xs">{formatISTDateTime(o.date)}</td>
                <td>{o.party_name}</td>
                <td className="text-xs text-slate-500">{o.job_site || '—'}</td>
                <td className="text-right tabular-nums">{num(o.total_qty, 1)}</td>
                <td className="text-right tabular-nums text-emerald-700">{num(o.delivered_qty, 1)}</td>
                <td className="text-right tabular-nums font-semibold">{num(o.pending_qty, 1)}</td>
                <td><span className={`tag ${STATUS_COLOR[o.status] || ''}`}>{o.status}</span></td>
                <td className="text-right"><ChevronRight size={14} className="text-slate-300" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail && <OrderDetailModal orderId={detail} onClose={() => setDetail(null)} onChanged={load} />}
      {creating && <OrderModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function OrderModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    date: today(), party_name: '', party_address: '', party_gstin: '',
    buyer_name: '', buyer_gstin: '', job_site: '', tax_type: 'GST Local 18%',
    payment_term: '', valid_date: '', cust_supply: false, remarks: '', place_of_supply: '',
    items: [{ grade: 'M25', pumping: 'Without Pump', uom: 'CUM', qty: '', rate: '', gst_pct: 18 }],
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setItem = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const addItem = () => setForm({ ...form, items: [...form.items, { grade: 'M25', pumping: 'Without Pump', uom: 'CUM', qty: '', rate: '', gst_pct: 18 }] });
  const delItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.party_name.trim()) return toast.error('Party name is required');
    if (form.items.some((i) => !i.qty || Number(i.qty) <= 0)) return toast.error('Each line needs a positive quantity');
    setSaving(true);
    try {
      await apiPost('/commercial/orders', {
        ...form,
        items: form.items.map((i) => ({ ...i, qty: Number(i.qty), rate: Number(i.rate || 0), gst_pct: Number(i.gst_pct || 18) })),
      });
      toast.success('Order created');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create order'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="New Sales Order" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Party (bill to) *"><input value={form.party_name} onChange={set('party_name')} className="input" autoFocus data-testid="order-party" /></Field>
          <GstinField label="Party GSTIN" value={form.party_gstin} onChange={set('party_gstin')} />
          <Field label="Job Site"><input value={form.job_site} onChange={set('job_site')} className="input" /></Field>
          <Field label="Buyer (ship to)"><input value={form.buyer_name} onChange={set('buyer_name')} className="input" placeholder="same as party if blank" /></Field>
          <GstinField label="Buyer GSTIN" value={form.buyer_gstin} onChange={set('buyer_gstin')}
            onStateResolved={(state) => setForm((f) => ({ ...f, place_of_supply: f.place_of_supply || state }))} />
          <Field label="Place of Supply"><input value={form.place_of_supply} onChange={set('place_of_supply')} className="input" placeholder="auto-fills from Buyer GSTIN" /></Field>
          <Field label="Payment Term"><input value={form.payment_term} onChange={set('payment_term')} className="input" placeholder="30 Days / Advance" /></Field>
        </div>
        <div className="border border-slate-200 rounded-sm">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="kpi-overline">Contracted Grades</span>
            <button onClick={addItem} className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1"><Plus size={11} /> Add grade</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead><tr><th>Grade</th><th>Pump</th><th className="text-right">Qty m³</th><th className="text-right">Rate ₹/m³</th><th className="text-right">Amount</th><th></th></tr></thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={i}>
                    <td><select value={it.grade} onChange={(e) => setItem(i, 'grade', e.target.value)} className="input !py-1">{GRADES.map((g) => <option key={g}>{g}</option>)}</select></td>
                    <td><select value={it.pumping} onChange={(e) => setItem(i, 'pumping', e.target.value)} className="input !py-1">{PUMPING.map((p) => <option key={p}>{p}</option>)}</select></td>
                    <td><input type="number" value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} className="input !py-1 w-20 text-right" /></td>
                    <td><input type="number" value={it.rate} onChange={(e) => setItem(i, 'rate', e.target.value)} className="input !py-1 w-24 text-right" /></td>
                    <td className="text-right tabular-nums text-xs">{num(Number(it.qty || 0) * Number(it.rate || 0), 0)}</td>
                    <td>{form.items.length > 1 && <button onClick={() => delItem(i)} className="text-slate-300 hover:text-red-600"><Trash2 size={12} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-1" data-testid="order-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Create Order'}
        </button>
      </div>
    </ModalShell>
  );
}

function OrderDetailModal({ orderId, onClose, onChanged }) {
  const [dd, setDd] = useState(null);
  const [order, setOrder] = useState(null);
  const [confirm, ConfirmHost] = useConfirm();

  const load = () => {
    Promise.all([apiGet(`/commercial/orders/${orderId}/drawdown`), apiGet(`/commercial/orders/${orderId}`)])
      .then(([d, o]) => { setDd(d); setOrder(o); }).catch(() => toast.error('Failed to load order'));
  };
  useEffect(load, [orderId]);

  const cancel = async () => {
    if (!(await confirm({ title: 'Cancel this order?', body: `Cancel ${dd?.order_no}? Only possible if nothing has been delivered.`, confirmLabel: 'Cancel Order', danger: true }))) return;
    try { await apiPost(`/commercial/orders/${orderId}/cancel`); toast.success('Order cancelled'); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to cancel'); }
  };

  if (!dd || !order) return <ModalShell title="Order" onClose={onClose}><div className="text-slate-400 text-sm py-6 text-center">Loading…</div></ModalShell>;

  return (
    <ModalShell title={`${dd.order_no} — ${dd.party_name}`} onClose={onClose} wide>
      {ConfirmHost}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className={`tag ${STATUS_COLOR[dd.status] || ''}`}>{dd.status}</span>
          {order.payment_term && <span className="text-xs text-slate-500">Payment: {order.payment_term}</span>}
          {order.job_site && <span className="text-xs text-slate-500">Site: {order.job_site}</span>}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[['Contracted', `${num(dd.total_contracted, 1)} m³`], ['Delivered', `${num(dd.total_delivered, 1)} m³`], ['Pending', `${num(dd.total_pending, 1)} m³`]].map(([l, v], i) => (
            <div key={l} className={`bg-white border rounded-sm p-3 ${i === 2 ? 'border-[#1F7A6E]' : 'border-slate-200'}`}>
              <div className="kpi-overline">{l}</div>
              <div className={`font-display text-lg font-extrabold ${i === 2 ? 'text-[#1F7A6E]' : ''}`}>{v}</div>
            </div>
          ))}
        </div>

        <div>
          <div className="kpi-overline mb-1">Grade Draw-down</div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full dense-table">
              <thead><tr><th>Grade</th><th>Pump</th><th className="text-right">Rate</th><th className="text-right">Contracted</th><th className="text-right">Delivered</th><th className="text-right">Pending</th></tr></thead>
              <tbody>
                {dd.lines.map((l) => (
                  <tr key={l.seq}>
                    <td className="font-display font-bold">{l.grade}</td>
                    <td className="text-xs">{l.pumping}</td>
                    <td className="text-right tabular-nums">{num(l.rate, 0)}</td>
                    <td className="text-right tabular-nums">{num(l.contracted_qty, 1)}</td>
                    <td className="text-right tabular-nums text-emerald-700">{num(l.delivered_qty, 1)}</td>
                    <td className="text-right tabular-nums font-semibold">{num(l.pending_qty, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {dd.challans.length > 0 && (
          <div>
            <div className="kpi-overline mb-1">Challans against this order ({dd.challans.length})</div>
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto max-h-48 overflow-y-auto">
              <table className="w-full dense-table">
                <thead><tr><th>Challan</th><th>Date</th><th>Grade</th><th className="text-right">Qty</th><th>Vehicle</th><th>Status</th></tr></thead>
                <tbody>
                  {dd.challans.map((c) => (
                    <tr key={c.challan_no}>
                      <td className="font-mono text-xs">{c.challan_no}</td>
                      <td className="text-xs">{formatISTDateTime(c.date)}</td>
                      <td>{c.grade}</td>
                      <td className="text-right tabular-nums">{num(c.qty_m3, 1)}</td>
                      <td className="font-mono text-xs">{c.vehicle_no}</td>
                      <td><span className={`tag ${c.status === 'cancelled' ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-700'}`}>{c.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {dd.status === 'open' && dd.total_delivered === 0 && (
          <button onClick={cancel} className="btn-secondary w-full justify-center text-red-600">Cancel Order</button>
        )}
      </div>
    </ModalShell>
  );
}

// ── Delivery Schedule ────────────────────────────────────────────────────────
function ScheduleTab() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirm, ConfirmHost] = useConfirm();

  const load = () => apiGet('/commercial/schedules', { date }).then(setData).catch(() => toast.error('Failed to load schedule'));
  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (s) => {
    if (!(await confirm({ title: 'Delete schedule?', body: `Delete ${s.schedule_no}?`, confirmLabel: 'Delete', danger: true }))) return;
    try { await apiDel(`/commercial/schedules/${s.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div>
      {ConfirmHost}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input w-auto" data-testid="sched-date" />
        <button onClick={() => setDate(today())} className="text-xs text-[#1F7A6E] hover:underline">Today</button>
        <div className="ml-auto text-xs text-slate-400">{data ? `${num(data.total_planned, 1)} m³ planned` : ''}</div>
        <button onClick={() => setCreating(true)}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="sched-new-btn"><Plus size={13} /> Schedule Pour</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Schedule No</th><th>Order</th><th>Party</th><th>Grade</th><th>Pump</th>
            <th>Start</th><th className="text-right">Planned</th><th className="text-right">Delivered</th><th className="text-right">Pending</th><th></th>
          </tr></thead>
          <tbody>
            {!data && <tr><td colSpan="10" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {data && data.items.length === 0 && <tr><td colSpan="10" className="text-center py-8 text-slate-400">No pours scheduled for this day.</td></tr>}
            {data && data.items.map((s) => (
              <tr key={s.id} data-testid={`sched-row-${s.id}`}>
                <td className="font-mono text-xs text-[#1F7A6E] font-semibold">{s.schedule_no}</td>
                <td className="font-mono text-xs">{s.order_no}</td>
                <td>{s.party_name}</td>
                <td className="font-display font-bold">{s.grade}</td>
                <td className="text-xs">{s.pumping}</td>
                <td className="text-xs">{s.start_time ? formatISTDateTime(s.start_time) : '—'}</td>
                <td className="text-right tabular-nums">{num(s.qty, 1)}</td>
                <td className="text-right tabular-nums text-emerald-700">{num(s.challan_qty, 1)}</td>
                <td className="text-right tabular-nums font-semibold">{num(s.pending_qty, 1)}</td>
                <td className="text-right">{s.challan_qty === 0 && <button onClick={() => remove(s)} className="text-slate-300 hover:text-red-600"><Trash2 size={13} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && <ScheduleModal date={date} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function ScheduleModal({ date, onClose, onSaved }) {
  const [lines, setLines] = useState([]);
  const [form, setForm] = useState({ line_key: '', date, qty: '', start_time: '', pump: 'Without Pump', pump_gang: '', supervisor: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { apiGet('/commercial/orders/open-lines').then((r) => setLines(r.items || [])).catch(() => {}); }, []);
  const picked = lines.find((l) => `${l.order_id}|${l.seq}` === form.line_key);

  const save = async () => {
    if (!picked) return toast.error('Pick an order line');
    if (!form.qty || Number(form.qty) <= 0) return toast.error('Enter a planned quantity');
    setSaving(true);
    try {
      await apiPost('/commercial/schedules', {
        order_id: picked.order_id, order_item_seq: picked.seq, date: form.date,
        qty: Number(form.qty), start_time: form.start_time ? `${form.date}T${form.start_time}:00` : null,
        pump: form.pump, pump_gang: form.pump_gang || null, supervisor: form.supervisor || null,
      });
      toast.success('Pour scheduled');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to schedule'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="Schedule a Pour" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Order Line (pending only)">
          <select value={form.line_key} onChange={set('line_key')} className="input" autoFocus data-testid="sched-line">
            <option value="">Select an order + grade…</option>
            {lines.map((l) => <option key={`${l.order_id}|${l.seq}`} value={`${l.order_id}|${l.seq}`}>{l.order_no} · {l.party_name} · {l.grade} · {num(l.pending_qty, 1)} m³ pending</option>)}
          </select>
          {lines.length === 0 && <div className="text-xs text-amber-600 mt-1">No open order lines — create a Sales Order first.</div>}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Planned Qty (m³)"><input type="number" value={form.qty} onChange={set('qty')} className="input" data-testid="sched-qty" /></Field>
          <Field label="Start Time"><input type="time" value={form.start_time} onChange={set('start_time')} className="input" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Pump"><select value={form.pump} onChange={set('pump')} className="input">{PUMPING.map((p) => <option key={p}>{p}</option>)}</select></Field>
          <Field label="Pump Gang"><input value={form.pump_gang} onChange={set('pump_gang')} className="input" /></Field>
          <Field label="Supervisor"><input value={form.supervisor} onChange={set('supervisor')} className="input" /></Field>
        </div>
        {picked && <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2">Rate ₹{num(picked.rate, 0)}/m³ inherited from order {picked.order_no}.</div>}
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-1" data-testid="sched-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Schedule Pour'}
        </button>
      </div>
    </ModalShell>
  );
}
